import { IFormSchemaField, IFormSchemaSection, IPriorFiling } from "../types";
import { getAttestationText } from "./formMetadata";

/** Type-appropriate empty default for a single (non-Group) field. */
export const fieldTypeDefaultValue = (field: IFormSchemaField): unknown => {
  switch (field.type) {
    case "Boolean":
    case "SignatureBlock":
      return false;
    case "Address":
      return {
        addressLine1: "",
        addressLine2: "",
        city: "",
        state: null,
        postalCode: ""
      };
    case "Number":
    case "Currency":
    case "Percent":
    case "Date":
      return null;
    case "Enum":
      // Multi-select Enum binds to WSControlGroup checkboxes and a
      // `Yup.array()` schema, so it must default to [] — defaulting to ""
      // breaks validation and the control. Single-select stays a string.
      return field.multi ? [] : "";
    default:
      return "";
  }
};

/**
 * An empty Group row seeded with each item field's type default — used both
 * for `minItems` seeding here and by the Group renderer's Add button (RHF v6
 * Controllers inside field arrays need a defined `defaultValue`).
 */
export const buildGroupRowDefaults = (
  itemFields: IFormSchemaField[]
): Record<string, unknown> => {
  const row: Record<string, unknown> = {};
  itemFields.forEach(itemField => {
    // Same precedence as top-level seeding: an item field's computed value,
    // then its prefill, then the type default — so a computed/read-only row
    // field starts populated (its auto-calculated box and the row payload
    // agree) instead of silently holding null.
    row[itemField.key] =
      itemField.operations.computed?.value ??
      itemField.prefill?.value ??
      fieldTypeDefaultValue(itemField);
  });
  return row;
};

/**
 * Seeds RHF default values from the forms schema: each field's resolved
 * `prefill.value` (when present) wins, otherwise a type-appropriate empty
 * default. Prior submission (`currentFiling.data`) is layered on next, then the
 * locally-persisted draft, so the most recent intent takes precedence.
 */
export const catalogToInitialValues = (params: {
  sections: IFormSchemaSection[];
  draft: Record<string, unknown> | null;
  currentFiling?: IPriorFiling;
  metadata?: unknown;
}): Record<string, unknown> => {
  const { sections, draft, currentFiling, metadata } = params;
  const values: Record<string, unknown> = {};
  let hasSignatureField = false;

  sections.forEach(section => {
    section.fields.forEach(field => {
      if (field.type === "SignatureBlock") hasSignatureField = true;
      // Repeating groups default to no rows (or `minItems` empty rows) —
      // arrays never seed from computed/prefill, which are scalar concepts.
      if (field.type === "Group") {
        const minItems = field.item?.minItems ?? 0;
        values[field.key] = Array.from({ length: minItems }, () =>
          buildGroupRowDefaults(field.item?.fields ?? [])
        );
        return;
      }
      // Auto-calculated fields seed from the engine's computed value so they are
      // populated (and pass required validation) on first render, before any
      // re-evaluation.
      const computedValue = field.operations.computed?.value;
      if (computedValue !== undefined && computedValue !== null) {
        values[field.key] = computedValue;
        return;
      }
      const prefillValue = field.prefill?.value;
      if (prefillValue !== undefined && prefillValue !== null) {
        values[field.key] = prefillValue;
        return;
      }
      values[field.key] = fieldTypeDefaultValue(field);
    });
  });

  if (currentFiling?.data) {
    Object.assign(values, currentFiling.data);
  }

  if (draft) {
    Object.assign(values, draft);
  }

  // Form-level consent fallback when no SignatureBlock field collects it.
  if (!hasSignatureField && getAttestationText(metadata)) {
    values.signature = { consentAccepted: false };
  }

  return values;
};
